/**
 * captureSidecar — builds the render-truth JSON twin of a capture frame
 * (see src/shared/diagnostics/CaptureSidecarTypes.ts for the why).
 *
 * Pure relevance filtering over an H2kRenderState snapshot + a
 * VisualLifecycle slice — no Pixi, no window, fully unit-testable. The
 * caller (AbilityCastCapture) supplies the snapshot and the lifecycle
 * array; this module decides what's relevant to THIS cast:
 *   - projectiles/effects owned by the caster or tagged with the ability
 *   - the local caster's cast bars
 *   - lifecycle events since cast start that name the ability (or name no
 *     ability at all — anonymous impact/feedback events inside the cast
 *     window are usually this cast's)
 */
import type {
  CaptureSidecar,
  SidecarLifecycleEvent,
  ChainSegment,
  TrailSample,
  BeamSample,
} from '../../shared/diagnostics/CaptureSidecarTypes';
import type { H2kRenderState } from '../testhooks/__h2k_render_state';
import type { VisualEvent } from './VisualLifecycleStore';

export interface BuildCaptureSidecarInput {
  castId: string;
  offsetMs: number;
  ability: string;
  targetId: string | null;
  /** Cast-start clock (Date.now() at the intent) — lifecycle window floor. */
  castStartTs: number;
  renderState: H2kRenderState;
  lifecycle: readonly VisualEvent[];
  /** Target world position resolved by the caller (findMobPosition) — used
   *  when the target isn't a player nameplate (mobs/dummies usually). */
  targetFallback?: { x: number; y: number } | null;
  /** Chain hops since cast start (ChainSegmentStore) — S2.F2. */
  chainSegments?: readonly ChainSegment[];
  /** Trail attach/sample/release + ghost rows (TrailStore) — trails spec §9. */
  trailSamples?: readonly TrailSample[];
  /** Beam-link endpoint samples (BeamLinkStore) — beam spec §7. */
  beamSamples?: readonly BeamSample[];
}

function toLifecycleEvent(e: VisualEvent): SidecarLifecycleEvent {
  return {
    id: e.id,
    timestamp: e.timestamp,
    type: e.type,
    label: e.label,
    abilityId: e.abilityId ?? null,
    targetId: e.targetId ?? null,
    projectileId: e.projectileId ?? null,
    school: e.school ?? null,
    source: e.source ?? null,
    isDuplicate: e.isDuplicate,
    x: e.x,
    y: e.y,
  };
}

/**
 * Grace before castStartTs for the lifecycle window. An INSTANT ability (a
 * melee swing) resolves its hit_vfx synchronously WITH the cast — measured
 * firing ~2ms BEFORE the capture records castStartTs. A strict `ts >=
 * castStartTs` floor then drops it (the cause of melee's missing impact). This
 * window catches events that fire on the same beat as the cast; the abilityId
 * match still scopes them to THIS cast (GCD/cooldown means no same-ability cast
 * lands within it).
 */
const CAST_START_GRACE_MS = 250;

export function buildCaptureSidecar(input: BuildCaptureSidecarInput): CaptureSidecar {
  const { renderState: rs } = input;
  const casterId = rs.localSessionId;
  const windowFloor = input.castStartTs - CAST_START_GRACE_MS;

  const relevant = (ownerId: string | null | undefined, abilityId: string | null | undefined): boolean =>
    (casterId != null && ownerId === casterId) || abilityId === input.ability;

  const casterPlate = rs.nameplates.find((n) => n.isLocal);
  const targetPlate = input.targetId
    ? rs.nameplates.find((n) => n.ownerId === input.targetId)
    : undefined;

  return {
    castId: input.castId,
    offsetMs: input.offsetMs,
    ts: rs.ts,
    casterId,
    targetId: input.targetId,
    caster:
      casterPlate && casterPlate.x != null && casterPlate.y != null
        ? { x: casterPlate.x, y: casterPlate.y }
        : null,
    target:
      targetPlate && targetPlate.x != null && targetPlate.y != null
        ? { x: targetPlate.x, y: targetPlate.y }
        : input.targetFallback ?? null,
    projectiles: rs.projectiles.filter((p) => relevant(p.ownerId, p.abilityId)),
    // Anonymous effects (no owner AND no ability tag) stay in: the effect-
    // player's persistent/orbiter/follower rows carry neither (they are
    // shape-level), yet a self-buff aura IS this cast's render truth —
    // the verifier judges them by position instead.
    effects: rs.effects.filter(
      (e) => relevant(e.ownerId, e.abilityId) || (e.ownerId == null && e.abilityId == null),
    ),
    castBars: rs.castBars.filter((c) => casterId != null && c.casterId === casterId),
    lifecycle: input.lifecycle
      .filter(
        (e) =>
          e.timestamp >= windowFloor
          && (e.abilityId === input.ability || e.abilityId == null),
      )
      .map(toLifecycleEvent),
    chainSegments: (input.chainSegments ?? [])
      .filter(
        (s) =>
          s.ts >= windowFloor
          && (s.abilityId === input.ability || s.abilityId == null),
      )
      .map((s): ChainSegment => ({ ...s })),
    trailSamples: (input.trailSamples ?? [])
      .filter(
        (s) =>
          s.ts >= windowFloor
          && (s.abilityId === input.ability || s.abilityId == null),
      )
      .map((s): TrailSample => ({ ...s })),
    beamSamples: (input.beamSamples ?? [])
      .filter(
        (s) =>
          s.ts >= windowFloor
          && (s.abilityId === input.ability || s.abilityId == null),
      )
      .map((s): BeamSample => ({ ...s, from: { ...s.from }, to: { ...s.to } })),
  };
}
